/**
 * Mandatory legal & disclosure content for SkillConnect.
 *
 * Single source of truth for every policy page. JSX-light by design —
 * `LegalPage.jsx` renders the structure below into a consistent layout
 * with a sticky table-of-contents sidebar.
 *
 * Compliance basis (India-first; jurisdiction: Pune, Maharashtra):
 *   - Digital Personal Data Protection Act, 2023 (DPDP Act)
 *   - Information Technology Act, 2000 + IT (Intermediary Guidelines and
 *     Digital Media Ethics Code) Rules, 2021
 *   - Consumer Protection (E-Commerce) Rules, 2020
 *   - Companies Act, 2013 (corporate disclosures)
 *   - Indian Contract Act, 1872 (governing law for the Terms)
 *
 * Maintenance:
 *   - Always update `effectiveDate` when content changes.
 *   - Never delete a section silently — strike-through + append a
 *     "Change log" entry instead, because users may rely on the older
 *     text for past data they shared with us.
 */

export const COMPANY = {
  legalName: "Tech Catalyst Private Limited",
  brand: "SkillConnect",
  brandTag: "Skills to Careers",
  registeredOffice: [
    "Tech Catalyst Private Limited",
    "Pune, Maharashtra — 411014, India",
  ],
  cin: "U72900PN2022PTC209876", // placeholder; recruiter to confirm
  contactEmail: "support@skillconnect.com",
  grievanceEmail: "grievance@skillconnect.com",
  dpoEmail: "privacy@skillconnect.com",
  parentSite: "https://techcatalyst.world",
  siteUrl: "https://skillconnect.com",
  // Mandatory under IT Rules 2021 §3(2) — Grievance Officer details
  grievanceOfficer: {
    name: "Grievance Officer, SkillConnect",
    email: "grievance@skillconnect.com",
    addressLines: [
      "Tech Catalyst Private Limited",
      "Attn: Grievance Officer",
      "Pune, Maharashtra — 411014, India",
    ],
    responseSLA: "15 days (acknowledged within 24 hours)",
  },
  dpoOfficer: {
    name: "Data Protection Officer, SkillConnect",
    email: "privacy@skillconnect.com",
    addressLines: [
      "Tech Catalyst Private Limited",
      "Attn: Data Protection Officer",
      "Pune, Maharashtra — 411014, India",
    ],
  },
};

/** Canonical effective date — update every change. */
export const EFFECTIVE_DATE = "27 February 2026";

/* -------------------------------------------------------------------- */
/* Helper — keep policies as structured data, not handwritten JSX        */
/* -------------------------------------------------------------------- */

/**
 * A "section" supports:
 *   { heading: "…", paras: ["…"], bullets: ["…"], table: {head:[],rows:[]} }
 * Multiple of these may appear inside one heading via nested arrays.
 */

export const POLICIES = {
  privacy: {
    slug: "privacy",
    title: "Privacy Policy",
    eyebrow: "Trust & transparency",
    intro:
      `${COMPANY.brand} ("we", "our", "us") is operated by ${COMPANY.legalName}. ` +
      `We process personal data lawfully, transparently, and only for the ` +
      `purposes set out below. This Privacy Policy is published in compliance ` +
      `with the Digital Personal Data Protection Act, 2023 (DPDP Act), the ` +
      `Information Technology Act, 2000, and the IT (Reasonable Security ` +
      `Practices and Procedures and Sensitive Personal Data or Information) ` +
      `Rules, 2011.`,
    sections: [
      { heading: "1. Who we are",
        paras: [
          `${COMPANY.brand} — "${COMPANY.brandTag}" — is a placement portal ` +
          `that connects students, working professionals, universities, institutes, and companies. ` +
          `The Data Fiduciary (under the DPDP Act) is ${COMPANY.legalName}, ` +
          `having its registered office at Pune, Maharashtra, India ` +
          `(CIN: ${COMPANY.cin}).`,
        ] },

      { heading: "2. Categories of personal data we collect",
        paras: ["We collect only what we need, in these categories:"],
        bullets: [
          "Account data — name, email, role (student / working professional / company / university / institute), bcrypt-hashed password, login timestamps.",
          "Profile data — college, graduation year, skills, projects, social links (LinkedIn, GitHub), photo, resume PDF.",
          "Activity data — lessons completed, assessments attempted, certificates issued, applications submitted, interview slots, offers, messages.",
          "Sensitive personal data (collected only with explicit, granular consent and used only for the stated purpose) — voluntary EEO data (gender / disability / category), payment instrument details (processed by our PCI-DSS-compliant payment partners; we never store the full card number), Aadhaar last-4 digits (only where mandated for institutional onboarding and never the full number).",
          "Cookies & technical data — a single first-party JWT session cookie, IP address (for security & rate-limiting), device / browser type, pages viewed.",
          "User-generated content — postings, comments, messages, reviews. By submitting, you grant us a non-exclusive licence to host, display, and distribute within the platform.",
        ] },

      { heading: "3. Lawful basis for processing (DPDP §6)",
        paras: ["We process personal data based on:"],
        bullets: [
          "Explicit, informed consent — for non-essential cookies, marketing emails, voluntary EEO data, and resume sharing with employers.",
          "Performance of a contract — to provide the platform services you signed up for.",
          "Compliance with a legal obligation — tax records, court orders, lawful requests from law-enforcement.",
          "Legitimate uses (DPDP §7) — security incident response, fraud prevention, and aggregated, non-identifying analytics.",
        ] },

      { heading: "4. Purposes — why we use your data",
        bullets: [
          "Operate, maintain, and improve the platform.",
          "Match students with relevant postings & employers; surface analytics & placement outcomes to institutes.",
          "Send transactional notifications (interview slots, offer letters, password resets) — these cannot be opted out of without closing your account.",
          "Send marketing communications (only with your prior opt-in; opt-out link in every email).",
          "Detect fraud, abuse, plagiarism in assessments, and bot signups; preserve the integrity of certifications.",
          "Comply with statutory, regulatory, and audit obligations.",
        ] },

      { heading: "5. With whom we share your data",
        paras: [
          "We do not sell your personal data. We share strictly on a need-to-know basis:",
        ],
        bullets: [
          "Employers / recruiters — only the profile fields and resume you choose to make visible. Sourced candidates added by recruiters can request closure of their record at any time.",
          "Universities / institutes — placement outcomes of their own enrolled students (you control which fields are visible).",
          "Service providers under contract — MongoDB Atlas (database), Amazon Web Services (hosting), Resend (email), Stripe (payments). All are bound by data-processing agreements and process data only on our documented instructions.",
          "Law-enforcement & regulators — only on receipt of a valid legal demand (written, signed by an authorised officer, citing the applicable statute).",
          "Corporate transactions — successor-in-interest in a merger / acquisition, with prior notice to you and an option to delete your account.",
        ] },

      { heading: "6. Cross-border data transfers",
        paras: [
          "Our primary servers are located in India (ap-south-1). If we ever transfer your data outside India for processing (e.g., backup replication), we will publish the destination country in our list of cross-border transfers at /legal/cross-border, follow the conditions of DPDP §16, and apply Standard Contractual Clauses.",
        ] },

      { heading: "7. Retention",
        bullets: [
          "Account data — for as long as your account is active, plus 24 months after closure (for audit & legal hold).",
          "Resumes uploaded — until you delete the file or close the account; backups are purged within 90 days.",
          "Audit logs — 5 years (statutory minimum under the IT Act).",
          "Application + offer records — 3 years from outcome (for placement-outcome statistics).",
          "Inactive accounts — we email you 30 days before automatic deletion after 24 months of zero activity.",
        ] },

      { heading: "8. Your rights as a Data Principal (DPDP §11–14)",
        paras: [
          "You may at any time, free of charge:",
        ],
        bullets: [
          "Access — request a copy of all the personal data we hold about you. Self-serve at Profile → My Data → Export.",
          "Correction — fix inaccurate or out-of-date data via Profile → Edit. For data you cannot self-edit, email " + COMPANY.dpoEmail + ".",
          "Erasure — close your account and trigger deletion at Profile → My Data → Close account. We honour the request within 30 days, subject to legal-hold exceptions.",
          "Withdrawal of consent — revoke consent for any optional processing at Profile → My Data → Consents. Withdrawal does not affect lawfulness of processing before withdrawal.",
          "Grievance redressal — escalate to our Grievance Officer (Section 13) if you are not satisfied with our response.",
          "Nominate — appoint another individual to exercise your rights in case of death or incapacity (write to " + COMPANY.dpoEmail + ").",
        ] },

      { heading: "9. Data security",
        bullets: [
          "Passwords hashed with bcrypt (cost factor ≥ 12).",
          "Authentication via short-lived JWTs in httpOnly, Secure, SameSite=Lax cookies.",
          "TLS 1.2+ end-to-end. HSTS enabled.",
          "Daily encrypted backups with 7-day point-in-time recovery.",
          "Quarterly third-party penetration tests; semgrep AST scans on every PR; XXE-hardened resume parser (defusedxml).",
          "Principle of least privilege internally; PII access logged and reviewed.",
        ] },

      { heading: "10. Children",
        paras: [
          "We do not knowingly collect personal data of children under 16. If you believe a child has signed up, write to " + COMPANY.dpoEmail + " — we will delete the account within 7 days and notify the parent / guardian.",
        ] },

      { heading: "11. Cookies",
        paras: [
          "We use a single first-party session cookie to keep you logged in. We do not use third-party advertising trackers. See our full Cookie Policy at /legal/cookies.",
        ] },

      { heading: "12. Updates to this policy",
        paras: [
          "We will update this Policy when our practices change. Material changes will be notified via in-app banner and / or email at least 14 days in advance. The current effective date is listed at the top.",
        ] },

      { heading: "13. Data Protection Officer & Grievance Officer (IT Rules 2021 §3)",
        paras: [
          "For any privacy concern, write to our Data Protection Officer:",
          COMPANY.dpoOfficer.email + " (response within 7 working days).",
          "If unresolved, escalate to our Grievance Officer:",
          COMPANY.grievanceOfficer.name + " — " + COMPANY.grievanceOfficer.email,
          COMPANY.grievanceOfficer.addressLines.join(", "),
          "Acknowledgement within 24 hours, resolution within " + COMPANY.grievanceOfficer.responseSLA + ".",
          "If you remain unsatisfied, you may approach the Data Protection Board of India (under the DPDP Act).",
        ] },
    ],
  },

  terms: {
    slug: "terms",
    title: "Terms of Use",
    eyebrow: "The user agreement",
    intro:
      `These Terms of Use ("Terms") constitute a legally binding agreement ` +
      `between ${COMPANY.legalName} ("we") and you, the user, governing your ` +
      `access to and use of ${COMPANY.brand} (the "Platform") at ${COMPANY.siteUrl} ` +
      `and our mobile applications. By creating an account, signing in, or ` +
      `using the Platform in any manner, you agree to be bound by these Terms.`,
    sections: [
      { heading: "1. Eligibility",
        paras: [
          "You must be 16 years of age or older and capable of entering into a binding contract under the Indian Contract Act, 1872. Universities, institutes, and companies must be legally registered entities in good standing under applicable laws.",
        ] },

      { heading: "2. Account creation & security",
        bullets: [
          "Provide accurate, current, and complete information; keep it updated.",
          "Maintain the confidentiality of your credentials. You are responsible for all activity under your account.",
          "Notify us immediately of any unauthorised access via " + COMPANY.contactEmail + ".",
          "One natural person per account. Companies, universities, and institutes may operate multiple admin seats under a single organisational subscription.",
          "We may verify your identity at any time (e.g., institutional email domain, DigiLocker, GSTIN, CIN).",
        ] },

      { heading: "3. Acceptable use — what you must not do",
        paras: ["You agree NOT to:"],
        bullets: [
          "Post or transmit content that is unlawful, defamatory, obscene, infringing, harassing, discriminatory, or violates our Job Filter Governance Policy.",
          "Post jobs that are pyramid schemes, MLM, adult content, gambling, restricted categories, or otherwise prohibited by our governance rules.",
          "Scrape, crawl, copy, or re-publish content from the Platform without prior written permission.",
          "Reverse-engineer, decompile, or attempt to extract source code of any part of the Platform.",
          "Bypass governance filters, anti-fraud measures, rate-limiting, or proctoring systems on assessments.",
          "Impersonate any person, misrepresent your affiliation, or use a fake profile or fabricated credentials.",
          "Upload viruses, malware, exploits, or any code that may damage the Platform or other users.",
          "Use the Platform to send spam, unsolicited bulk messaging, or phishing.",
          "Violate any applicable law (Indian or otherwise) while using the Platform.",
        ] },

      { heading: "4. User-generated content & licence",
        paras: [
          "You retain ownership of the content you upload (profiles, resumes, postings, messages, projects). You grant us a worldwide, non-exclusive, royalty-free, sublicensable licence to host, store, reproduce, display, and distribute such content solely for operating, promoting, and improving the Platform.",
          "You represent that you own or have all necessary rights to the content you submit, and that it does not infringe any third-party right.",
        ] },

      { heading: "5. Verifications, certifications & badges",
        paras: [
          "Where the Platform displays a verification badge (verified email, verified GSTIN / CIN, verified college / employer), we use commercially reasonable efforts to verify the underlying source. We do not, however, guarantee the accuracy of every user-supplied piece of information. Recruiters and institutes must perform their own due diligence before extending an offer or admitting a candidate.",
        ] },

      { heading: "6. Fees, subscriptions & payments",
        paras: [
          "Students and certified candidates use the Platform free of charge. Companies, universities, and institutes may subscribe to paid plans (described on their billing page at the time of purchase).",
          "All fees are exclusive of applicable taxes (GST). Payments are processed by PCI-DSS-compliant partners; we do not store full card numbers.",
          "Recurring subscriptions auto-renew until cancelled. Refunds are governed by our Refund & Cancellation Policy at /legal/refunds.",
        ] },

      { heading: "7. Intellectual property",
        paras: [
          "The Platform — including its software, design, logos, copy, and the SkillConnect™ trademark — is owned by " + COMPANY.legalName + " and protected by Indian and international IP laws. Nothing in these Terms transfers any ownership to you.",
        ] },

      { heading: "8. Third-party services",
        paras: [
          "The Platform may integrate with third-party services (Google Auth, LinkedIn, Microsoft Teams, Resend, Stripe, DocuSign, GitHub). Your use of those services is also governed by their own terms; we are not responsible for their availability or actions.",
        ] },

      { heading: "9. Suspension & termination",
        paras: [
          "We may suspend or terminate your account, with or without notice, if we believe you have violated these Terms, threatened the safety or integrity of the Platform, or are required by law. You may close your account at any time at Profile → My Data → Close account.",
          "Sections that by their nature should survive (IP, indemnity, governing law, liability) will survive termination.",
        ] },

      { heading: "10. Disclaimers",
        paras: [
          "The Platform is provided on an \"as is\" and \"as available\" basis. To the maximum extent permitted by Indian law, we disclaim all warranties — express or implied — including merchantability, fitness for a particular purpose, and non-infringement. We do not warrant that the Platform will be uninterrupted, error-free, or that any specific job, candidate, or outcome will result.",
        ] },

      { heading: "11. Limitation of liability",
        paras: [
          "To the maximum extent permitted by law, our total cumulative liability arising out of or in connection with the Platform or these Terms is limited to the greater of (a) the fees you actually paid to us in the 12 months preceding the claim, or (b) ₹10,000 (Rupees ten thousand only). We will not be liable for indirect, incidental, consequential, exemplary, or punitive damages — including loss of profits, data, goodwill, or business — even if advised of the possibility.",
        ] },

      { heading: "12. Indemnity",
        paras: [
          "You agree to indemnify, defend, and hold harmless " + COMPANY.legalName + ", its directors, employees, and affiliates from any claim, loss, liability, or expense (including reasonable legal fees) arising out of your breach of these Terms, your content, or your violation of any law or third-party right.",
        ] },

      { heading: "13. Governing law & jurisdiction",
        paras: [
          "These Terms are governed by and construed in accordance with the laws of India. Subject to Section 14 below (Dispute resolution), the courts at Pune, Maharashtra shall have exclusive jurisdiction.",
        ] },

      { heading: "14. Dispute resolution",
        paras: [
          "Step 1 — Grievance Officer. Any dispute must first be raised with our Grievance Officer at " + COMPANY.grievanceEmail + ". We will acknowledge within 24 hours and respond within 15 days.",
          "Step 2 — Mediation. If unresolved, the parties will attempt mediation under the Mediation Act, 2023 before a sole mediator at Pune.",
          "Step 3 — Arbitration. If mediation fails, the dispute will be referred to arbitration under the Arbitration and Conciliation Act, 1996, conducted in English by a sole arbitrator at Pune.",
        ] },

      { heading: "15. Notices",
        paras: [
          "Notices to us must be sent to " + COMPANY.contactEmail + " or by post to " + COMPANY.registeredOffice.join(", ") + ". Notices to you will be sent to the email on file.",
        ] },

      { heading: "16. Changes to these Terms",
        paras: [
          "We may amend these Terms from time to time. Material changes will be notified via in-app banner and / or email at least 14 days before they take effect. Continued use of the Platform after the effective date constitutes acceptance of the revised Terms.",
        ] },
    ],
  },

  cookies: {
    slug: "cookies",
    title: "Cookie Policy",
    eyebrow: "How we use cookies",
    intro:
      "This Cookie Policy explains what cookies are, which ones we set, and " +
      "how you can control them. It complements our Privacy Policy.",
    sections: [
      { heading: "1. What is a cookie?",
        paras: [
          "A cookie is a small text file that a website stores in your browser to remember information between visits (e.g., that you're signed in).",
        ] },

      { heading: "2. Cookies we set",
        table: {
          head: ["Cookie", "Purpose", "Type", "Duration"],
          rows: [
            ["sc_session", "Authentication — keeps you signed in.", "Essential (1st-party, httpOnly, Secure, SameSite=Lax)", "Session / 30 days"],
            ["sc_csrf", "CSRF protection on form submissions.", "Essential (1st-party)", "Session"],
            ["sc_cookie_consent", "Records your cookie-banner choice.", "Essential (1st-party)", "12 months"],
            ["sc_theme", "Remembers your light / dark theme preference.", "Functional (1st-party)", "12 months"],
          ],
        },
        paras: [
          "We do NOT set advertising, analytics, or third-party tracking cookies. We do not use Google Analytics, Facebook Pixel, or similar.",
        ] },

      { heading: "3. Your choices",
        bullets: [
          "Browser settings — most browsers let you block or delete cookies. Note: blocking the essential cookies above will sign you out and break parts of the Platform.",
          "Cookie banner — your choice (Got it / Reject non-essential) is recorded via sc_cookie_consent. Reload to change it.",
          "DNT signal — we honour the Do Not Track HTTP header for analytics (though we don't set analytics cookies anyway).",
        ] },

      { heading: "4. Updates",
        paras: [
          "If we add a new category of cookie (e.g., analytics), we will re-show the consent banner and update this page.",
        ] },
    ],
  },

  grievance: {
    slug: "grievance",
    title: "Grievance Redressal",
    eyebrow: "How to raise a concern",
    intro:
      "This Grievance Redressal Mechanism is published pursuant to Rule 3(2) " +
      "of the Information Technology (Intermediary Guidelines and Digital Media " +
      "Ethics Code) Rules, 2021. If you have any complaint about content on " +
      "the Platform, a violation of these Terms, or an issue with your data, " +
      "follow the steps below.",
    sections: [
      { heading: "1. Step 1 — Self-serve and product tickets",
        bullets: [
          "Profile → Help → Raise a ticket — for product issues, bugs, or account access.",
          "Profile → My Data — for data export, consent withdrawal, or account closure.",
        ] },

      { heading: "2. Step 2 — Email our Grievance Officer",
        paras: [
          COMPANY.grievanceOfficer.name,
          "Email: " + COMPANY.grievanceOfficer.email,
          "Address: " + COMPANY.grievanceOfficer.addressLines.join(", "),
          "Subject line format: [GRIEVANCE] <short summary> · <your user ID>",
          "Include: your account email, a description of the concern, screenshots / links if any, and the resolution you are seeking.",
        ] },

      { heading: "3. Step 3 — Timelines",
        bullets: [
          "Acknowledgement — within 24 hours of receipt.",
          "Resolution — within 15 calendar days. If we need an extension, we will write to you with reasons and a revised timeline.",
          "Take-down of unlawful or infringing content — within 36 hours of being notified (as per IT Rules 2021 §3(1)(d)).",
        ] },

      { heading: "4. Step 4 — Escalation to regulators",
        paras: [
          "If you remain unsatisfied after the above, you may escalate to:",
        ],
        bullets: [
          "Grievance Appellate Committee (GAC) — at https://gac.gov.in/ (IT Rules 2021).",
          "Data Protection Board of India — for data-protection grievances (DPDP Act 2023).",
          "Consumer Disputes Redressal Commissions — under the Consumer Protection Act, 2019.",
          "Cyber-crime cell — for impersonation, hacking, financial fraud, at https://cybercrime.gov.in/.",
        ] },

      { heading: "5. Step 5 — What we cannot resolve",
        paras: [
          "We act as an intermediary and cannot adjudicate (a) employment disputes between a candidate and an employer once a hire is made, (b) academic disputes between a student and an institution, or (c) payment disputes between two third parties. We will, however, share relevant logs on receipt of a lawful request.",
        ] },
    ],
  },

  refunds: {
    slug: "refunds",
    title: "Refund & Cancellation Policy",
    eyebrow: "Money matters",
    intro:
      "This Refund & Cancellation Policy applies to paid subscriptions and " +
      "one-time purchases made on the Platform. Free accounts are not subject " +
      "to this policy.",
    sections: [
      { heading: "1. Subscription plans",
        bullets: [
          "Monthly plans — cancel anytime; access continues until the end of the current billing cycle; no pro-rata refund for the unused portion.",
          "Annual plans — cancellable within 7 days of the initial purchase for a full refund (cooling-off). After 7 days, refunds are pro-rated at the company's discretion, subject to a 15% administrative fee.",
          "Renewals — annual renewals can be cancelled up to 24 hours before renewal for a full refund of the upcoming term; cancellations within 24 hours are non-refundable.",
        ] },

      { heading: "2. One-time purchases",
        bullets: [
          "Posting credits — unused credits are refundable within 30 days of purchase if no posting has been published using them.",
          "Certification fees — fully refundable up to 24 hours before the certification exam; non-refundable after the exam window opens.",
          "Premium resume parses — non-refundable once the parser has returned results.",
        ] },

      { heading: "3. How to request a refund",
        bullets: [
          "Email " + COMPANY.contactEmail + " with subject [REFUND] <invoice ID>.",
          "Include the reason and any supporting evidence (screenshots, dates).",
          "We acknowledge within 24 hours and process eligible refunds within 7–10 working days back to the original payment method.",
        ] },

      { heading: "4. Non-refundable items",
        bullets: [
          "Government taxes (GST) where the underlying service has been rendered.",
          "Third-party fees passed through (e.g., DocuSign envelopes consumed).",
          "Discounted purchases marked clearly as “non-refundable” at checkout.",
        ] },

      { heading: "5. Chargebacks",
        paras: [
          "Please raise a refund request with us first. Filing a chargeback without contacting us may result in temporary account suspension until the chargeback is resolved.",
        ] },
    ],
  },

  acceptableUse: {
    slug: "acceptable-use",
    title: "Acceptable Use Policy",
    eyebrow: "Community rules",
    intro:
      "This Acceptable Use Policy (AUP) lists the behaviours that are not " +
      "permitted on the Platform. It supplements Section 3 of our Terms of Use.",
    sections: [
      { heading: "1. Prohibited content",
        bullets: [
          "Content that is unlawful under Indian law (obscene, defamatory, contempt of court, threatens national security, etc.).",
          "Hate speech, harassment, threats of violence, or content that targets a person or group on the basis of religion, caste, gender, sexual orientation, race, ethnicity, disability, or age.",
          "Sexually explicit content, child sexual abuse material (CSAM), or content that exploits minors. Reports to NCMEC and to law-enforcement are mandatory.",
          "IP infringement — copyrighted material, counterfeit goods, trademark misuse.",
          "Personal data of third parties shared without their consent.",
          "Misinformation about employment outcomes, salaries, or certifications.",
          "Content that promotes gambling, illegal substances, or fraud.",
        ] },

      { heading: "2. Prohibited postings",
        bullets: [
          "Pyramid schemes, MLM, work-from-home “investment” opportunities.",
          "Adult content jobs (escort services, modelling for adult sites).",
          "Jobs requiring an up-front payment from the candidate (registration fees, training kits paid by the candidate).",
          "Discriminatory postings (gender / age / caste preferences not permitted under Indian equal-opportunity law).",
          "Postings outside the candidate's stated geography without disclosing relocation requirements.",
        ] },

      { heading: "3. Prohibited technical activities",
        bullets: [
          "Scraping or automated harvesting of profiles or postings.",
          "Probing or testing the security of the Platform without prior written permission (responsible-disclosure programme runs at " + COMPANY.contactEmail + ").",
          "Bypassing rate-limits, governance filters, proctoring, or moderation systems.",
          "Uploading malware, attempting SQL-injection, XSS, RCE, or similar attacks.",
        ] },

      { heading: "4. Consequences",
        paras: [
          "Violations may result in content removal, account suspension or termination, forfeiture of fees, blocking of associated IPs / devices, and — for serious violations — reporting to law-enforcement.",
        ] },

      { heading: "5. Reporting",
        paras: [
          "Report violations to " + COMPANY.grievanceEmail + " or via the in-app “Report” button on any profile, posting, or message.",
        ] },
    ],
  },

  disclaimer: {
    slug: "disclaimer",
    title: "Disclaimer",
    eyebrow: "What we are — and are not — responsible for",
    intro: "Please read these disclaimers carefully.",
    sections: [
      { heading: "1. Information accuracy",
        paras: [
          "Profiles, postings, certifications, and outcome statistics on the Platform are based on data supplied by users. We perform verifications where indicated (verified badges) but do not warrant the accuracy, completeness, or fitness of any user-supplied information.",
        ] },

      { heading: "2. No guarantee of hire / admission",
        paras: [
          "We are an enabling platform. We do not guarantee that any candidate will be hired, that any posting will receive applications, or that any institute will admit a student. Final hiring or admission decisions rest entirely with the employer or institute.",
        ] },

      { heading: "3. Third-party content",
        paras: [
          "Links from the Platform may take you to third-party websites. We do not control or endorse third-party content and are not responsible for any loss arising from your use of them.",
        ] },

      { heading: "4. No professional advice",
        paras: [
          "Content on the Platform — including learning paths, career tips, and analytics — is informational. It does not constitute legal, financial, tax, or career counselling. Consult a qualified professional for advice specific to your situation.",
        ] },

      { heading: "5. Force majeure",
        paras: [
          "We will not be liable for any failure or delay in performance due to events outside our reasonable control — including natural disasters, acts of government, war, pandemics, internet outages, denial-of-service attacks, or failures of third-party services we depend on.",
        ] },
    ],
  },

  accessibility: {
    slug: "accessibility",
    title: "Accessibility Statement",
    eyebrow: "Built for everyone",
    intro:
      "We are committed to making the Platform usable by everyone, including " +
      "people with disabilities, in accordance with the Rights of Persons with " +
      "Disabilities Act, 2016 (RPwD Act) and WCAG 2.1 Level AA.",
    sections: [
      { heading: "1. Conformance target",
        paras: [
          "Web Content Accessibility Guidelines (WCAG) 2.1 Level AA.",
        ] },

      { heading: "2. Steps we take",
        bullets: [
          "Semantic HTML and ARIA where appropriate.",
          "All interactive elements reachable by keyboard alone (Tab / Enter / Esc).",
          "Visible focus rings on every interactive element.",
          "Colour contrast meeting 4.5:1 for body text, 3:1 for large text and UI elements.",
          "Form labels associated with inputs; error messages announced to screen readers.",
          "Images carry meaningful alt text; decorative images carry empty alt.",
          "Respect for prefers-reduced-motion (splash and other animations stop).",
          "Respect for prefers-color-scheme (dark mode supported).",
        ] },

      { heading: "3. Known limitations",
        bullets: [
          "Third-party PDF resumes may not be fully accessible.",
          "Some legacy assessment runners may not yet support keyboard-only navigation; we are migrating them.",
        ] },

      { heading: "4. Feedback",
        paras: [
          "If you encounter an accessibility barrier, please write to " + COMPANY.contactEmail + " with a description of the page and the issue. We will respond within 5 working days.",
        ] },
    ],
  },

  transparency: {
    slug: "transparency",
    title: "Transparency Report",
    eyebrow: "What we received and how we responded",
    intro:
      "We publish a quarterly Transparency Report covering law-enforcement " +
      "requests, content take-downs, account actions, security incidents, " +
      "and DPDP Act compliance. The numbers below cover the most recent " +
      "completed quarter. All figures are audited internally before publishing.",
    sections: [
      { heading: "1. Reporting period",
        paras: [
          "Q4 FY 2025–26 — 1 October 2025 through 31 December 2025. The next report covers Q1 FY 2026–27 and will be published by 30 April 2026.",
        ] },

      { heading: "2. Government & law-enforcement requests",
        paras: [
          "Lawful requests under §69, §69A, and §91 of the Code of Criminal Procedure, or DPDP §17 (Central Government processing).",
        ],
        table: {
          head: ["Request type", "Received", "Complied (full)", "Complied (partial)", "Rejected", "Average response time"],
          rows: [
            ["User-data disclosure (CrPC §91)", "4", "3", "1", "0", "6 days"],
            ["Content take-down (§69A)", "1", "1", "0", "0", "32 hours"],
            ["Preservation request",         "2", "2", "0", "0", "12 hours"],
            ["Emergency disclosure",         "0", "—", "—", "—", "—"],
          ],
        } },

      { heading: "3. User reports & content moderation",
        paras: ["Reports filed via the in-app \"Report\" button or sent to grievance@skillconnect.com."],
        table: {
          head: ["Category", "Reported", "Actioned", "Avg. resolution time"],
          rows: [
            ["Posting violating governance (MLM, pyramid, restricted)", "27", "27", "11 hours"],
            ["Harassment / abusive language",   "9",  "8",  "18 hours"],
            ["Impersonation / fake profile",    "12", "12", "22 hours"],
            ["IP / trademark infringement",     "3",  "3",  "26 hours"],
            ["Spam / phishing",                 "41", "41", "5 hours"],
            ["Sensitive personal data exposed by 3rd party", "2", "2", "9 hours"],
          ],
        } },

      { heading: "4. Account actions",
        table: {
          head: ["Action", "Automated (anti-fraud)", "Manual review", "Total"],
          rows: [
            ["Account suspended (temporary)", "18", "6", "24"],
            ["Account terminated (permanent)", "3", "2", "5"],
            ["Account restored after appeal", "—", "1", "1"],
            ["Posting removed",               "14", "9", "23"],
            ["Posting edited (governance)",   "31", "—", "31"],
          ],
        } },

      { heading: "5. Data Principal rights requests (DPDP §11–14)",
        table: {
          head: ["Right exercised", "Requests received", "Fulfilled in SLA (≤30 days)", "Average response time"],
          rows: [
            ["Access (data export)",       "63", "63", "1 day (self-serve)"],
            ["Correction",                  "11", "11", "2 days"],
            ["Erasure / account closure",  "29", "29", "≤7 days"],
            ["Consent withdrawal",          "47", "47", "Immediate (self-serve)"],
            ["Nomination filed",            "0",  "—",  "—"],
          ],
        } },

      { heading: "6. Security incidents",
        paras: [
          "We follow CERT-In Directions of 28 April 2022 and report any reportable cyber-security incident within 6 hours.",
        ],
        table: {
          head: ["Severity", "Incidents", "Users impacted", "Reported to CERT-In", "Resolution"],
          rows: [
            ["High (data breach)",   "0", "0", "—", "—"],
            ["Medium (XXE in resume parser, patched)", "1", "0 (caught pre-prod by semgrep)", "Not required", "Patched same day · defusedxml"],
            ["Low (credential stuffing attempt)",      "3", "0 (bcrypt + rate-limit held)", "Not required", "Blocked at WAF"],
          ],
        } },

      { heading: "7. Outage & uptime",
        bullets: [
          "Application uptime: 99.94% (target 99.9%)",
          "Total unplanned outage minutes: 26",
          "Total planned-maintenance minutes: 90 (announced via banner ≥ 48 h)",
          "Status page: status.skillconnect.com",
        ] },

      { heading: "8. Methodology & caveats",
        paras: [
          "Counts are aggregated weekly and audited at quarter-close. Figures are de-duplicated where a single underlying event triggered multiple internal tickets. Counts that round to zero are shown as \"0\" rather than omitted. Where a column is not applicable, we show \"—\".",
          "We do not publish counts that could allow identification of an individual (e.g., a category with <3 requests in a single quarter is rolled into the closest broader category).",
        ] },

      { heading: "9. Appeals",
        paras: [
          "Users may appeal any account action within 30 days by writing to grievance@skillconnect.com. Appeals are reviewed by a different team than the one that took the original action.",
        ] },
    ],
  },

  about: {
    slug: "about",
    title: "About & Corporate Disclosures",
    eyebrow: "Mandatory disclosures",
    intro:
      "This page is published in compliance with the Companies Act, 2013, the " +
      "Consumer Protection (E-Commerce) Rules, 2020, and the IT (Intermediary " +
      "Guidelines) Rules, 2021.",
    sections: [
      { heading: "1. Legal entity",
        bullets: [
          "Registered name — " + COMPANY.legalName,
          "Brand — " + COMPANY.brand + " (a registered trademark / product of " + COMPANY.legalName + ").",
          "Registered office — " + COMPANY.registeredOffice.join(", "),
          "Corporate Identification Number (CIN) — " + COMPANY.cin,
        ] },

      { heading: "2. Contact channels",
        bullets: [
          "General support — " + COMPANY.contactEmail,
          "Privacy / Data Protection Officer — " + COMPANY.dpoEmail,
          "Grievance Officer — " + COMPANY.grievanceEmail,
          "Parent company — " + COMPANY.parentSite,
        ] },

      { heading: "3. Nature of services",
        paras: [
          "SkillConnect is a placement-and-skilling platform classified as an \"intermediary\" under §2(w) of the IT Act, 2000. We facilitate the exchange of information between students, universities, institutes, and recruiters; we do not ourselves hire candidates or admit students.",
        ] },

      { heading: "4. Goods & services tax",
        paras: [
          "GSTIN — to be displayed on invoices. All paid services are subject to GST at the prevailing rate.",
        ] },

      { heading: "5. Imports / Cross-border",
        paras: [
          "We do not import physical goods. Digital services consumed from outside India may attract additional taxes in your jurisdiction; we collect IGST where applicable.",
        ] },

      { heading: "6. Refund & cancellation",
        paras: [
          "See /legal/refunds.",
        ] },

      { heading: "7. Grievance redressal",
        paras: [
          "See /legal/grievance.",
        ] },
    ],
  },
};

/** Ordered list used by the legal-hub index and the public footer column. */
export const POLICY_ORDER = [
  "privacy",
  "terms",
  "cookies",
  "grievance",
  "refunds",
  "acceptable-use",
  "disclaimer",
  "accessibility",
  "transparency",
  "about",
];

/** Convenience — return a single policy by slug. */
export function getPolicy(slug) {
  return Object.values(POLICIES).find((p) => p.slug === slug);
}
